export default class WebSocketClient {
	static socketConnections = 0;
	type = 'clientSideWebSocket';
	isSocketConnected = false;
	pendingRequests = [];
	reconnectTimeout = 2000;
	trackingState = {};

	constructor(options = {}) {
		Object.assign(this, options);

		if (!this.socketUrl)
			this.socketUrl = (location.protocol == 'https:' ? 'wss://' : 'ws://') + location.host;

		window.addEventListener('online', () => this.connectWebSocket());
		window.addEventListener('offline', () => this.goOffline());

		if (!this.socket && navigator.onLine) this.connectWebSocket();
	}

	connectWebSocket() {
		if (!navigator.onLine) return;
		this.socket = new WebSocket(this.socketUrl);
		this.socket.onopen = this.onOpen.bind(this);
		this.socket.onerror = this.onError.bind(this);
		this.socket.onmessage = this.onMessage.bind(this);
		this.socket.onclose = this.onClose.bind(this);
	}

	onOpen() {
		WebSocketClient.socketConnections++;
		l('WEB SOCKET CONNECTION #' + WebSocketClient.socketConnections + ' ESTABLISHED');
		this.isSocketConnected = true;
		this.pendingRequests.forEach((payload, i) => {
			this.send(payload);
			delete this.pendingRequests[i];
		});

		// Resend tracking state on reconnect
		if (Object.keys(this.trackingState).length > 0) {
			this.send({ type: 'TRACKING', payload: this.trackingState });
		}

		// Push offline changes on reconnect
		if (provider.sync) {
			provider.sync();
		}

		observer.trigger('webSocketClient.reconnect', this);
		this._startHeartbeat();
	}

	onError(error) {
		console.error(error);
	}

	onMessage(e) {
		provider.receive(JSON.parse(e.data), e.data.length);
	}

	onClose(e) {
		this._stopHeartbeat();
		this.isSocketConnected = false;
		if ((typeof e.wasClean == 'undefined' || e.wasClean === false) && navigator.onLine) {
			this._reconnectTimer = setTimeout(() => {
				this.connectWebSocket();
			}, this.reconnectTimeout);
		}
	}

	goOffline() {
		this._stopHeartbeat();
		this.isSocketConnected = false;
		this.pendingRequests = []; // we dont want to replay all socket requests when we loose connection
		clearTimeout(this._reconnectTimer);
		if (this.socket) {
			this.socket.onclose = null;
			this.socket.close();
		}
	}

	close() {
		this._stopHeartbeat();
		this.socket.close();
	}

	_startHeartbeat() {
		this._stopHeartbeat();
		this._heartbeatInterval = setInterval(() => {
			if (this.socket?.readyState === WebSocket.OPEN) {
				this.socket.send(JSON.stringify({ type: 'PING' }));
			}
		}, 30000);
	}

	_stopHeartbeat() {
		clearInterval(this._heartbeatInterval);
	}

	send(payload) {
		try {
			if (this.isSocketConnected) {
				this.socket.send(JSON.stringify(payload));
			} else {
				this.pendingRequests.push(payload);
			}
		} catch (error) {
			console.error(error, payload);
		}
	}

	/**
	 * Replace the entire tracking state and send to server.
	 * Used by adapters after navigation when the chain changes.
	 *
	 * @param {object} state - Full tracking state (replaces previous)
	 */
	setTracking(state) {
		this.trackingState = state;
		this.send({ type: 'TRACKING', payload: this.trackingState });
	}

	/**
	 * Merge into tracking state and send to server.
	 * Used by controllers for continuous updates (viewport, etc.).
	 *
	 * @param {object} state - Key/value pairs to merge into tracking
	 */
	sendTracking(state) {
		Object.assign(this.trackingState, state);
		this.send({ type: 'TRACKING', payload: this.trackingState });
	}

	/**
	 * Clear tracking keys (e.g., when leaving a view).
	 *
	 * @param  {...string} keys - Keys to remove from tracking state
	 */
	clearTracking(...keys) {
		const cleared = {};
		for (const key of keys) {
			delete this.trackingState[key];
			cleared[key] = null;
		}
		this.send({ type: 'TRACKING', payload: cleared });
	}
}
